import { Link } from "react-router-dom";
import { Boxes } from "lucide-react";

import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { AddOnCard } from "@/components/addons/AddOnCard";
import { useAddOns } from "@/hooks/useAddOns";
import type { AddOnDto } from "@/types/api";

export function AddOnsPage() {
  const { data, isLoading, error } = useAddOns();

  // Group by scope; sort each bucket by price ascending.
  const account   = (data ?? [])
    .filter(a => a.active && a.scope === "Account")
    .sort((a, b) => a.price_monthly - b.price_monthly);
  const container = (data ?? [])
    .filter(a => a.active && a.scope === "Container")
    .sort((a, b) => a.price_monthly - b.price_monthly);

  return (
    <div className="space-y-5 sm:space-y-6">
      <div>
        <h1 className="font-display text-2xl font-bold tracking-tight sm:text-3xl">Add-ons</h1>
        <p className="mt-1 text-sm text-muted-foreground sm:text-base">
          Extra capacity for your account or a specific domain — applied the moment payment clears.
        </p>
      </div>

      {error ? (
        <Card>
          <CardContent className="py-10 text-center text-sm text-muted-foreground">
            Couldn't load the add-on catalogue. Check the API and refresh.
          </CardContent>
        </Card>
      ) : isLoading ? (
        <>
          <SkeletonGrid />
          <SkeletonGrid />
        </>
      ) : (!data || data.length === 0) ? (
        <EmptyState />
      ) : (
        <>
          <Section
            eyebrow="Per domain"
            title="Container add-ons"
            sub="Attach to one specific domain — extra mailboxes, more bulk emails, that kind of thing."
            items={container}
          />
          <Section
            eyebrow="Account-wide"
            title="Account add-ons"
            sub="Extend the pool every domain on your account draws from."
            items={account}
          />
        </>
      )}
    </div>
  );
}

function Section({
  eyebrow,
  title,
  sub,
  items
}: {
  eyebrow: string;
  title: string;
  sub: string;
  items: AddOnDto[];
}) {
  if (items.length === 0) {
    return (
      <section className="space-y-3">
        <SectionHeader eyebrow={eyebrow} title={title} sub={sub} />
        <Card>
          <CardContent className="py-8 text-center text-sm text-muted-foreground">
            Nothing in this category yet.
          </CardContent>
        </Card>
      </section>
    );
  }
  return (
    <section className="space-y-4">
      <SectionHeader eyebrow={eyebrow} title={title} sub={sub} />
      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
        {items.map(a => <AddOnCard key={a.slug} addOn={a} />)}
      </div>
    </section>
  );
}

function SectionHeader({ eyebrow, title, sub }: { eyebrow: string; title: string; sub: string }) {
  return (
    <div>
      <p className="font-mono text-xs font-bold uppercase tracking-[0.18em] text-brand-accentDark">
        {eyebrow}
      </p>
      <h2 className="mt-1 font-display text-xl font-bold tracking-tight sm:text-2xl">{title}</h2>
      <p className="mt-1 text-sm text-muted-foreground">{sub}</p>
    </div>
  );
}

function SkeletonGrid() {
  return (
    <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
      {Array.from({ length: 3 }).map((_, i) => (
        <div key={i} className="rounded-xl border border-border bg-card p-6 shadow-brand-sm">
          <Skeleton className="h-3 w-16" />
          <Skeleton className="mt-2 h-6 w-32" />
          <Skeleton className="mt-3 h-4 w-48" />
          <Skeleton className="mt-4 h-9 w-24" />
          <div className="mt-5 space-y-2">
            <Skeleton className="h-3 w-full" />
            <Skeleton className="h-3 w-3/4" />
          </div>
          <Skeleton className="mt-6 h-10 w-full" />
        </div>
      ))}
    </div>
  );
}

function EmptyState() {
  return (
    <Card>
      <CardContent className="flex flex-col items-center gap-3 py-12 text-center">
        <div className="flex h-12 w-12 items-center justify-center rounded-full bg-brand-accentSoft text-primary">
          <Boxes className="h-6 w-6" />
        </div>
        <div>
          <p className="font-display text-lg font-semibold">No add-ons available yet</p>
          <p className="mt-1 max-w-sm text-sm text-muted-foreground">
            The catalogue is empty. Add some via the admin API or come back later.
          </p>
        </div>
        <Button asChild variant="outline">
          <Link to="/plans">Browse plans instead</Link>
        </Button>
      </CardContent>
    </Card>
  );
}
